import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
// Note: Using custom alert styling since shadcn/ui alert component is not available
import { Input } from "../../../ui/input";
import { Tooltip } from "../../../ui/tooltip";
import FinetuneConfirmationModal from "./modals/FinetuneConfirmationModal";
import {
  DownloadIcon,
  InboxIcon,
  AlertCircleIcon,
  EditIcon,
  RefreshCwIcon,
  PlayIcon,
  LoaderIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  XIcon,
  DatabaseIcon,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { FlowData, SimpleFlowFormat } from "../utils/types";
import { isSimpleFormat, validateSimpleFormat, convertSimpleFormatToFlowData } from "../utils/utils";
import { usePermissions } from "../../../../PermissionsProvider";
import { ConversationBuilderService } from "../../../../services/ConversationBuilderService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { CompanyService } from "../../../../services/CompanyService";

interface ConversationTree {
  id: number;
  conversation_flow_name: string;
  conversation_flow_version: number;
  is_latest: boolean;
  created_at: string;
  application_name: string;
  intent_name: string;
  conversation_flow_count?: number;
  status?: string;
}

// Tree status types
type TreeStatus =
  | "UNSAVED"
  | "SAVED"
  | "BUILDING_RESOURCES"
  | "READY_FOR_FINE_TUNE"
  | "FINETUNING"
  | "RUNNING"
  | "FAILED"
  | "COMPLETED";

interface ConfigTabProps {
  onExportFlow: () => void;
  onImportFlow: (flowData: FlowData) => void;
  onLoad: () => void;
  onLoadTree: (tree: ConversationTree) => void;
  onClearDecisionVariables: () => void;
  onTriggerFinetune: () => void;
  systemPrompt: string;
  onSystemPromptChange: (prompt: string) => void;
  loadedTreeMetadata?: {
    id: number;
    conversation_flow_name: string;
    application_name: string;
    intent_name: string;
    conversation_flow_version: number;
    is_latest: boolean;
    status?: string;
  } | null;
  // New props for tree status management
  currentTreeStatus: TreeStatus;
  currentTreeName: string;
  onTreeStatusChange?: (status: TreeStatus) => void;
  onTreeNameChange?: (name: string, skipValidation?: boolean) => void;
}

// Helper function to format numbers with K/M abbreviations
const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return "-";

  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return num.toString();
  }
};

// Helper function to get status styling
const getStatusStyling = (status: TreeStatus) => {
  switch (status) {
    case "UNSAVED":
      return {
        bgColor: "bg-muted",
        textColor: "text-muted-foreground",
        borderColor: "border-muted",
        icon: AlertCircleIcon,
      };
    case "SAVED":
      return {
        bgColor: "bg-accent/20",
        textColor: "text-foreground",
        borderColor: "border-border",
        icon: CheckCircleIcon,
      };
    case "BUILDING_RESOURCES":
      return {
        bgColor: "bg-accent/30",
        textColor: "text-foreground",
        borderColor: "border-border",
        icon: LoaderIcon,
      };
    case "READY_FOR_FINE_TUNE":
      return {
        bgColor: "bg-maitai-green/10",
        textColor: "text-maitai-green",
        borderColor: "border-maitai-green/20",
        icon: DatabaseIcon,
      };
    case "FINETUNING":
      return {
        bgColor: "bg-accent/30",
        textColor: "text-foreground",
        borderColor: "border-border",
        icon: LoaderIcon,
      };
    case "RUNNING":
      return {
        bgColor: "bg-accent/30",
        textColor: "text-foreground",
        borderColor: "border-border",
        icon: LoaderIcon,
      };
    case "COMPLETED":
      return {
        bgColor: "bg-maitai-green/10",
        textColor: "text-maitai-green",
        borderColor: "border-maitai-green/20",
        icon: CheckCircleIcon,
      };
    case "FAILED":
      return {
        bgColor: "bg-accent/20",
        textColor: "text-muted-foreground",
        borderColor: "border-border",
        icon: XCircleIcon,
      };
    default:
      return {
        bgColor: "bg-muted",
        textColor: "text-muted-foreground",
        borderColor: "border-muted",
        icon: AlertCircleIcon,
      };
  }
};

const ConfigTab: React.FC<ConfigTabProps> = ({
  onExportFlow,
  onImportFlow,
  onLoad: _onLoad,
  onLoadTree,
  // onClearDecisionVariables,
  onTriggerFinetune,
  systemPrompt,
  onSystemPromptChange,
  loadedTreeMetadata,
  currentTreeStatus,
  currentTreeName,
  onTreeStatusChange,
  onTreeNameChange,
}) => {
  const { companyId, apiKey } = usePermissions();
  const [importError, setImportError] = useState<string | null>(null);
  const [importData, setImportData] = useState<FlowData | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isFinetuneModalVisible, setIsFinetuneModalVisible] = useState(false);
  const [trees, setTrees] = useState<ConversationTree[]>([]);
  const [loadingTrees, setLoadingTrees] = useState(false);
  const [, setIsGeneratingData] = useState(false);

  // Tree name editing state
  const [isEditingTreeName, setIsEditingTreeName] = useState(false);
  const [editingTreeName, setEditingTreeName] = useState(currentTreeName);
  const [isValidatingName, setIsValidatingName] = useState(false);

  // Track selected tree status to avoid timing issues
  const [selectedTreeStatus, setSelectedTreeStatus] =
    useState<TreeStatus | null>(null);

  // Update editing name when currentTreeName changes
  useEffect(() => {
    if (!isEditingTreeName) {
      setEditingTreeName(currentTreeName);
    }
  }, [currentTreeName, isEditingTreeName]);

  // Clear selectedTreeStatus when loadedTreeMetadata is updated
  useEffect(() => {
    if (loadedTreeMetadata?.status) {
      setSelectedTreeStatus(null);
    }
  }, [loadedTreeMetadata]);

  // Handle tree name editing
  const handleStartEditingTreeName = () => {
    setIsEditingTreeName(true);
    setEditingTreeName(currentTreeName);
  };

  const handleSaveTreeName = async () => {
    if (!editingTreeName.trim() || editingTreeName === currentTreeName) {
      setIsEditingTreeName(false);
      return;
    }

    if (!loadedTreeMetadata?.id || !companyId || !apiKey || !onTreeNameChange) {
      toast.error("Unable to save name change - missing configuration");
      setIsEditingTreeName(false);
      return;
    }

    
    setIsValidatingName(true);

    try {
      // Step 1: Get application and intent IDs for validation
      const application = await new Promise<any>((resolve, reject) => {
        const sub = CompanyService.getApplicationsByCompany(
          companyId,
          null,
          apiKey
        ).subscribe({
          next: (apps: any[]) => {
            sub.unsubscribe();
            const app = apps.find(
              (a) =>
                a.application_name === loadedTreeMetadata.application_name ||
                a.application_ref_name === loadedTreeMetadata.application_name
            );
            if (app) {
              resolve(app);
            } else {
              reject(new Error("Application not found"));
            }
          },
          error: (err) => {
            sub.unsubscribe();
            reject(err);
          },
        });
      });

      const intent = await new Promise<any>((resolve, reject) => {
        const sub = ApplicationService.getApplicationActions(
          application.id,
          null,
          apiKey
        ).subscribe({
          next: (actions: any[]) => {
            sub.unsubscribe();
            const action = actions.find(
              (a) => a.action_type === loadedTreeMetadata.intent_name
            );
            if (action) {
              resolve(action);
            } else {
              reject(new Error("Intent not found"));
            }
          },
          error: (err) => {
            sub.unsubscribe();
            reject(err);
          },
        });
      });

      
      // Step 2: VALIDATE - Check for conflicts BEFORE renaming
      toast.info("Validating tree name...");

      try {
        await new Promise<void>((resolve, reject) => {
          const sub =
            ConversationBuilderService.validateConversationFlowName(
              companyId,
              application.id,
              intent.id,
              editingTreeName.trim(),
              apiKey,
              1, // agent_id - match what's used when saving trees
              loadedTreeMetadata.id // exclude_config_id - exclude current tree from validation
            ).subscribe({
              next: () => {
                sub.unsubscribe();
                resolve();
              },
              error: (err) => {
                sub.unsubscribe();
                reject(err);
              },
            });
        });
      } catch (validationError: any) {
        // Handle validation failure
        let errorMessage = "";

        // Try to extract error message from various possible structures
        if (typeof validationError?.error === "string") {
          errorMessage = validationError.error;
        } else if (
          validationError?.error?.error &&
          typeof validationError.error.error === "string"
        ) {
          errorMessage = validationError.error.error;
        } else if (
          validationError?.error?.message &&
          typeof validationError.error.message === "string"
        ) {
          errorMessage = validationError.error.message;
        } else if (
          validationError?.message &&
          typeof validationError.message === "string"
        ) {
          errorMessage = validationError.message;
        } else if (typeof validationError === "string") {
          errorMessage = validationError;
        }

        if (errorMessage) {
          if (errorMessage.toLowerCase().includes("already exists")) {
            toast.error(`${errorMessage}`);
          } else {
            toast.error(`Validation failed: ${errorMessage}`);
          }
        } else {
          toast.error("Name validation failed. Please try a different name.");
        }

        // Reset editing name to original on validation failure
        setEditingTreeName(currentTreeName);
        setIsValidatingName(false);
        setIsEditingTreeName(false);
        return;
      }

      // Step 3: SAVE - Validation passed, now update the name
      const response = ConversationBuilderService.updateConversationFlowName(
        loadedTreeMetadata.id,
        editingTreeName.trim(),
        apiKey
      );

      const subscription = response.subscribe({
        next: (_result: any) => {
          toast.success(`Tree name updated to "${editingTreeName.trim()}"`);
          onTreeNameChange(editingTreeName.trim(), true); // Skip validation since we already validated
          // Refresh the versions table
          handleRefreshTrees();
          setIsEditingTreeName(false);
        },
        error: (error: any) => {
          // Since validation passed, this is likely a different error
          if (error.message && typeof error.message === "string") {
            toast.error(`Failed to update tree name: ${error.message}`);
          } else {
            toast.error("Failed to update conversation flow name");
          }

          // Reset editing name to original on save failure
          setEditingTreeName(currentTreeName);
          setIsEditingTreeName(false);
        },
      });

      // Clean up subscription
      setTimeout(() => subscription.unsubscribe(), 5000);
    } catch (_error) {
      toast.error("Failed to update tree name. Please try again.");

      // Reset editing name to original on general failure
      setEditingTreeName(currentTreeName);
      setIsEditingTreeName(false);
    } finally {
      setIsValidatingName(false);
    }
  };

  const handleCancelEditingTreeName = () => {
    setEditingTreeName(currentTreeName);
    setIsEditingTreeName(false);
    setIsValidatingName(false); // Reset validation state on cancel
  };

  const handleTreeNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveTreeName();
    } else if (e.key === "Escape") {
      handleCancelEditingTreeName();
    }
  };

  // Determine the actual status with proper priority
  const actualStatus =
    (loadedTreeMetadata?.status as TreeStatus) ||
    selectedTreeStatus ||
    currentTreeStatus;

  const navigate = useNavigate();
  const [, setIsTriggeringFinetune] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  // Calculate pagination
  const totalPages = Math.ceil(trees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrees = trees.slice(startIndex, endIndex);

  // Reset to first page when trees change
  useEffect(() => {
    setCurrentPage(1);
  }, [trees.length]);

  // Load trees automatically when component mounts
  useEffect(() => {
    if (!companyId || !apiKey) return;

    // Only load versions if we have the required metadata
    if (
      loadedTreeMetadata?.application_name &&
      loadedTreeMetadata?.intent_name &&
      loadedTreeMetadata?.conversation_flow_name
    ) {
      setLoadingTrees(true);

      const response = ConversationBuilderService.listConversationTreeVersions(
        companyId,
        loadedTreeMetadata.application_name,
        loadedTreeMetadata.intent_name,
        loadedTreeMetadata.conversation_flow_name,
        apiKey
      );

      const subscription = response.subscribe({
        next: (data: ConversationTree[]) => {
          setTrees(data);
          setLoadingTrees(false);
        },
        error: (_error: any) => {
          toast.error("Failed to load conversation tree versions");
          setLoadingTrees(false);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    } else {
      // No metadata available, show empty state
      setTrees([]);
      setLoadingTrees(false);
    }
  }, [companyId, apiKey, loadedTreeMetadata]);

  // Handle file upload for import
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsValidating(true);
    setImportError(null);
    setImportData(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);

        let flowData: FlowData;

        // Check if it's simple format
        if (isSimpleFormat(parsed)) {
          // Validate simple format
          if (!parsed.startNode) {
            throw new Error("Invalid simple flow format. Missing required 'startNode' field.");
          }
          
          if (!Array.isArray(parsed.nodes) || parsed.nodes.length === 0) {
            throw new Error("Invalid simple flow format. Missing or empty nodes array.");
          }
          
          // Validate nodes
          for (const node of parsed.nodes) {
            if (!node.type || !node.identifier) {
              throw new Error("Invalid node in simple format. Each node must have type and identifier.");
            }
            
            if (!["bot", "user", "decision"].includes(node.type)) {
              throw new Error(`Invalid node type "${node.type}". Must be "bot", "user", or "decision".`);
            }
            
            // Bot nodes must have content OR tool calls (not both)
            if (node.type === "bot") {
              const hasContent = typeof node.content === "string" && node.content.trim() !== "";
              const hasToolCalls = node.toolCalls && node.toolCalls.length > 0;
              if (!hasContent && !hasToolCalls) {
                throw new Error(`Bot node "${node.identifier}" must have either content or tool calls.`);
              }
              if (hasContent && hasToolCalls) {
                throw new Error(`Bot node "${node.identifier}" cannot have both content and tool calls.`);
              }
            }
            
            // User nodes must have content
            if (node.type === "user" && (typeof node.content !== "string" || node.content.trim() === "")) {
              throw new Error(`User node "${node.identifier}" must have content.`);
            }
            
            // Decision nodes cannot have content
            if (node.type === "decision" && node.content !== undefined && node.content !== null) {
              throw new Error(`Decision node "${node.identifier}" cannot have content.`);
            }
            
            if (node.type === "decision" && (!node.conditions || !Array.isArray(node.conditions))) {
              throw new Error("Decision nodes must have conditions array.");
            }
          }
          
          // Check if startNode exists
          if (!parsed.nodes.find((n: any) => n.identifier === parsed.startNode || n.id === parsed.startNode)) {
            throw new Error(`Start node "${parsed.startNode}" not found in nodes array.`);
          }
          
          // Validate simple format
          const validation = validateSimpleFormat(parsed as SimpleFlowFormat);
          if (!validation.valid) {
            throw new Error(`Simple format validation failed:\n${validation.errors.join("\n")}`);
          }
          
          // Convert simple format to full format
          flowData = convertSimpleFormatToFlowData({ ...parsed, nodes: parsed.nodes.map((n: any) => ({ ...n, content: typeof n.content === "string" ? n.content : "" })) } as SimpleFlowFormat);
          toast.success("Simple format detected and converted! ✨");
        } else {
          // Validate full format structure
          if (!parsed.data) {
            throw new Error(
              "Invalid flow file format. Missing required 'data' field."
            );
          }

          if (!parsed.data.nodes || !Array.isArray(parsed.data.nodes)) {
            throw new Error(
              "Invalid flow file format. Missing or invalid nodes data."
            );
          }

          if (!parsed.data.edges || !Array.isArray(parsed.data.edges)) {
            throw new Error(
              "Invalid flow file format. Missing or invalid edges data."
            );
          }

          // Validate node structure
          for (const node of parsed.data.nodes) {
            if (!node.id || !node.data) {
              throw new Error("Invalid node structure in flow file.");
            }
          }

          // Generate version and timestamp if missing
          flowData = {
            version: parsed.version || "1.0.0",
            timestamp: parsed.timestamp || new Date().toISOString(),
            metadata: {
              ...(parsed.metadata ?? {}),
              status: parsed.metadata?.status || "draft",
            },
            data: {
              nodes: parsed.data.nodes || [],
              edges: parsed.data.edges || [],
              globalVariableRegistry: parsed.data.globalVariableRegistry || {},
              decisionVariablesRegistry:
                parsed.data.decisionVariablesRegistry || {},
              identifiers: parsed.data.identifiers || [],
              interjectionGroups: parsed.data.interjectionGroups || [],
              nodeIdCounter: parsed.data.nodeIdCounter || 0,
              isFirstNode: parsed.data.isFirstNode || false,
              systemPrompt: parsed.data.systemPrompt,
            },
          };
        }

        setImportData(flowData);
        setImportError(null);
      } catch (err) {
        setImportError(
          err instanceof Error ? err.message : "Failed to parse flow file"
        );
        setImportData(null);
      } finally {
        setIsValidating(false);
      }
    };

    reader.onerror = () => {
      setImportError("Failed to read file");
      setIsValidating(false);
    };

    reader.readAsText(file);
  };

  // Handle import confirmation
  const handleImportConfirm = () => {
    if (importData) {
      onImportFlow(importData);
      setImportData(null);
      setImportError(null);
    }
  };

  // Debug function to clear all localStorage data and reset to blank canvas
  // const handleClearDecisionVariables = () => {
  //   onClearDecisionVariables();
  //   message.success("All data cleared - reset to blank canvas");
  // };

  // Handle finetune trigger
  const handleTriggerFinetune = () => {
    setIsFinetuneModalVisible(true);
  };

  // Handle finetune confirmation
  const handleFinetuneConfirm = () => {
    setIsFinetuneModalVisible(false);
    onTriggerFinetune();
    // Refresh the Load Versions table after triggering finetune
    // Note: This refreshes immediately after trigger, not after completion
    setTimeout(() => {
      handleRefreshTrees();
    }, 1000); // Small delay to allow backend to update
  };

  // Handle finetune cancel
  const handleFinetuneCancel = () => {
    setIsFinetuneModalVisible(false);
  };

  // Handle refreshing trees manually
  const handleRefreshTrees = () => {
    if (!companyId || !apiKey) return;

    // Only refresh versions if we have the required metadata
    if (
      loadedTreeMetadata?.application_name &&
      loadedTreeMetadata?.intent_name &&
      loadedTreeMetadata?.conversation_flow_name
    ) {
      setLoadingTrees(true);

      const response = ConversationBuilderService.listConversationTreeVersions(
        companyId,
        loadedTreeMetadata.application_name,
        loadedTreeMetadata.intent_name,
        loadedTreeMetadata.conversation_flow_name,
        apiKey
      );

      const subscription = response.subscribe({
        next: (data: ConversationTree[]) => {
          setTrees(data);
          setLoadingTrees(false);
        },
        error: (_error: any) => {
          toast.error("Failed to load conversation tree versions");
          setLoadingTrees(false);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    } else {
      // No metadata available, nothing to refresh
      setTrees([]);
      setLoadingTrees(false);
    }
  };

  const handleSelectTree = (tree: ConversationTree) => {
    // Immediately set the selected tree's status to avoid timing issues
    setSelectedTreeStatus((tree.status as TreeStatus) || null);
    onLoadTree(tree);
  };

  // Handle Generate Data button click
  const handleGenerateData = async () => {
    if (!loadedTreeMetadata || !companyId || !apiKey) {
      toast.error("Missing required data for generating data");
      return;
    }

    setIsGeneratingData(true);

    try {
      const response =
        ConversationBuilderService.createConversationTreeResources(
          companyId,
          loadedTreeMetadata.application_name,
          loadedTreeMetadata.intent_name,
          loadedTreeMetadata.conversation_flow_name,
          loadedTreeMetadata.id.toString(),
          apiKey
        );

      const subscription = response.subscribe({
        next: (_data: any) => {
          toast.success("Data generation successful");
          // Update tree status to READY_FOR_FINE_TUNE
          if (onTreeStatusChange) {
            onTreeStatusChange("READY_FOR_FINE_TUNE");
          }
          // Refresh the Load Versions table to show updated status
          handleRefreshTrees();
          setIsGeneratingData(false);
        },
        error: (_error: any) => {
          toast.error("Failed to generate data");
          setIsGeneratingData(false);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    } catch (_error) {
      toast.error("Error generating data");
      setIsGeneratingData(false);
    }
  };

  // Handle Trigger Finetune button click
  const handleTriggerFinetuneNew = async () => {
    if (!loadedTreeMetadata || !companyId || !apiKey) {
      toast.error("Missing required data for triggering finetune");
      return;
    }

    setIsTriggeringFinetune(true);

    try {
      const response =
        ConversationBuilderService.createConversationTreeFinetune(
          companyId,
          loadedTreeMetadata.application_name,
          loadedTreeMetadata.intent_name,
          loadedTreeMetadata.conversation_flow_name,
          loadedTreeMetadata.id.toString(),
          apiKey
        );

      const subscription = response.subscribe({
        next: (_data: any) => {
          toast.success("Finetune triggered successfully");
          // Update tree status to RUNNING
          if (onTreeStatusChange) {
            onTreeStatusChange("RUNNING");
          }
          // Refresh the Load Versions table to show updated status
          handleRefreshTrees();
          setIsTriggeringFinetune(false);
        },
        error: (_error: any) => {
          toast.error("Failed to trigger finetune");
          setIsTriggeringFinetune(false);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    } catch (_error) {
      toast.error("Error triggering finetune");
      setIsTriggeringFinetune(false);
    }
  };

  return (
    <div className="h-[calc(100vh-160px)] overflow-y-auto p-4">
      {/* Tree Status Indicator - Only visible when a tree is loaded */}
      {loadedTreeMetadata && (
        <Card className="mb-6 border-border">
          <CardContent className="p-6 overflow-visible">
            <div
              className={cn(
                "grid items-center gap-4",
                isEditingTreeName ? "grid-cols-1" : "grid-cols-[1fr_auto_1fr]"
              )}>
              {/* Column 1: Tree Name and Metadata */}
              <div
                className={cn(
                  "flex items-center",
                  isEditingTreeName ? "gap-4 justify-between" : "gap-2"
                )}>
                {isEditingTreeName ? (
                  <>
                    <Input
                      value={editingTreeName}
                      onChange={(e) => setEditingTreeName(e.target.value)}
                      onKeyDown={handleTreeNameKeyDown}
                      onBlur={handleSaveTreeName}
                      disabled={isValidatingName}
                      className="text-sm font-gc-med h-8 px-3 py-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      autoFocus
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleSaveTreeName}
                        disabled={isValidatingName}
                        className="h-8 w-8 p-0 hover:bg-maitai-green/10 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isValidatingName ? (
                          <RefreshCwIcon className="w-4 h-4 animate-spin text-maitai-green" />
                        ) : (
                          <CheckIcon className="w-4 h-4 text-maitai-green" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEditingTreeName}
                        disabled={isValidatingName}
                        className="h-8 w-8 p-0 hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed">
                        <XIcon className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span
                      className="text-maitai-green font-gc-med text-sm cursor-pointer hover:text-maitai-green/80 transition-colors"
                      onClick={handleStartEditingTreeName}>
                      {currentTreeName}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleStartEditingTreeName}
                      className="h-5 w-5 p-0 hover:bg-accent">
                      <EditIcon className="w-2.5 h-2.5 text-muted-foreground" />
                    </Button>
                    {loadedTreeMetadata && (
                      <span className="text-muted-foreground text-xs">
                        v{loadedTreeMetadata.conversation_flow_version}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Column 2: Status Pill (Centered) - Hidden during editing */}
              {!isEditingTreeName && (
                <div className="flex justify-self-center">
                  <div
                    className={cn(
                      "inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-gc-med transition-colors",
                      getStatusStyling(actualStatus).bgColor,
                      getStatusStyling(actualStatus).textColor,
                      getStatusStyling(actualStatus).borderColor
                    )}>
                    {(() => {
                      const StatusIcon = getStatusStyling(actualStatus).icon;
                      return (
                        <>
                          <StatusIcon
                            className={cn(
                              "w-3 h-3",
                              (actualStatus === "RUNNING" ||
                                actualStatus === "BUILDING_RESOURCES" ||
                                actualStatus === "FINETUNING") &&
                                "animate-spin"
                            )}
                          />
                          {(() => {
                            switch (actualStatus) {
                              case "READY_FOR_FINE_TUNE":
                                return "Ready";
                              case "UNSAVED":
                                return "Unsaved";
                              case "SAVED":
                                return "Saved";
                              case "BUILDING_RESOURCES":
                                return "Building";
                              case "FINETUNING":
                                return "Finetuning";
                              case "RUNNING":
                                return "Running";
                              case "COMPLETED":
                                return "Complete";
                              case "FAILED":
                                return "Failed";
                              default:
                                return String(actualStatus).replace(/_/g, " ");
                            }
                          })()}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Column 3: Execute Button (Right-aligned) - Hidden during editing */}
              {!isEditingTreeName && (
                <div className="flex justify-self-end">
                  <Tooltip
                    content={
                      actualStatus === "UNSAVED"
                        ? "Save your tree before executing to build a model"
                        : "Execute this conversation tree to build a model"
                    }>
                    <Button
                      onClick={() => {
                        if (loadedTreeMetadata?.id) {
                          navigate(
                            `/forge/conversation-trees/${loadedTreeMetadata.id}/execute`
                          );
                        }
                      }}
                      disabled={
                        actualStatus === "UNSAVED" || !loadedTreeMetadata?.id
                      }
                      className="h-8 px-4 bg-foreground hover:bg-foreground/90 text-background disabled:opacity-50 disabled:cursor-not-allowed">
                      <PlayIcon className="w-3 h-3 mr-2" />
                      Execute
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Prompt Section */}
      <Card className="mb-6 border-border">
        <CardHeader className="pb-2 border-b border-border">
          <CardTitle className="text-sm font-gc-med text-foreground">
            System Prompt
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-muted-foreground text-xs mb-2">
            Set the system prompt for your conversation.
          </p>

          <textarea
            value={systemPrompt}
            onChange={(e) => onSystemPromptChange(e.target.value)}
            placeholder="Enter your system prompt here... This will be sent as the first message to establish context and behavior for the AI assistant."
            rows={3}
            className="w-full p-2 text-xs border border-input rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-maitai-green focus:border-transparent"
          />

          {systemPrompt && (
            <p className="text-muted-foreground text-xs mt-1">
              {systemPrompt.length} characters
            </p>
          )}
        </CardContent>
      </Card>

      {/* Manage Flows Section */}
      <Card className="mb-6 border-border">
        <CardHeader className="pb-2 border-b border-border">
          <CardTitle className="text-sm font-gc-med text-foreground">
            Manage Trees
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Import Section */}
          <div className="mb-4">
            <h5 className="text-sm font-gc-med text-foreground mb-2">
              Import Tree
            </h5>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="h-16 flex items-center justify-center border-2 border-dashed border-border rounded-md hover:border-maitai-green hover:bg-accent/20 transition-colors duration-200 cursor-pointer">
                <div className="flex items-center gap-2">
                  <InboxIcon className="w-5 h-5 transition-colors duration-200" />
                  <span className="text-sm transition-colors duration-200">
                    Drop JSON or Click Upload
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <div className="mb-6">
            <Button
              onClick={onExportFlow}
              className="w-full h-8 bg-foreground hover:bg-foreground/90 text-background">
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export Current Tree
            </Button>
          </div>

          {/* Import Status Messages */}
          {importError && (
            <div className="mt-2 p-3 border border-destructive/50 bg-destructive/10 rounded-md flex items-start gap-2">
              <AlertCircleIcon className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <strong>Import Error:</strong> {importError}
              </div>
            </div>
          )}

          {importData && !importError && (
            <div className="mt-2 p-3 border border-maitai-green/20 bg-maitai-green/5 rounded-md flex items-start gap-2">
              <AlertCircleIcon className="h-4 w-4 text-maitai-green flex-shrink-0 mt-0.5" />
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">
                  <strong>Flow Ready to Import:</strong>{" "}
                  {importData.data.nodes.length} nodes,{" "}
                  {importData.data.edges.length} edges
                </span>
                <Button
                  onClick={handleImportConfirm}
                  disabled={isValidating}
                  className="ml-2 h-6 px-2 bg-foreground hover:bg-foreground/90 text-background">
                  {isValidating ? "Loading..." : "Import"}
                </Button>
              </div>
            </div>
          )}

          {/* Load Tree Section */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-gc-med text-foreground">
                Load Versions
              </h5>
              <Button
                onClick={handleRefreshTrees}
                disabled={loadingTrees}
                className="h-6 px-2 bg-foreground hover:bg-foreground/90 text-background">
                {loadingTrees ? (
                  <RefreshCwIcon className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCwIcon className="w-3 h-3" />
                )}
              </Button>
            </div>

            <p className="text-muted-foreground text-xs mb-4">
              Select a version of this conversation flow to load into the
              editor.
            </p>

            {/* Flow Name Display */}
            {loadedTreeMetadata?.conversation_flow_name && (
              <div className="mb-4 p-3 bg-muted/30 rounded-md border border-border/50">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-gc-med text-foreground">
                    Flow:
                  </span>
                  <span className="text-sm font-gc-reg text-foreground">
                    {loadedTreeMetadata.conversation_flow_name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({loadedTreeMetadata.application_name} -{" "}
                    {loadedTreeMetadata.intent_name})
                  </span>
                </div>
              </div>
            )}

            {/* Trees List - Always Visible */}
            <div className="border border-border rounded-md">
              {loadingTrees ? (
                <div className="p-4 text-center">
                  <RefreshCwIcon className="w-4 h-4 animate-spin mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    Loading trees...
                  </p>
                </div>
              ) : trees.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    You don't have any versions yet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Save the tree to see it here
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border hover:bg-transparent">
                      <TableHead className="text-xs font-gc-med text-muted-foreground py-4 px-2 text-center">
                        Version
                      </TableHead>
                      <TableHead className="text-xs font-gc-med text-muted-foreground py-4 px-2 text-center">
                        Latest
                      </TableHead>
                      <TableHead className="text-xs font-gc-med text-muted-foreground py-4 px-2 text-center">
                        Paths
                      </TableHead>
                      <TableHead className="text-xs font-gc-med text-muted-foreground py-4 px-2 text-center">
                        Created
                      </TableHead>
                      <TableHead className="text-xs font-gc-med text-muted-foreground py-4 px-2 text-center">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTrees.map((tree) => {
                      const isSelected = loadedTreeMetadata?.id === tree.id;
                      return (
                        <TableRow
                          key={tree.id}
                          className={cn(
                            "cursor-pointer transition-all duration-200 border-b border-border/50",
                            isSelected &&
                              "bg-maitai-green/5 border-maitai-green/20"
                          )}
                          onClick={() => handleSelectTree(tree)}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = "#86efac";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = "";
                            }
                          }}>
                          <TableCell className="py-4 px-2 text-center">
                            <span className="text-xs font-gc-reg text-foreground">
                              {tree.conversation_flow_version}
                            </span>
                          </TableCell>
                          <TableCell className="py-4 px-2 text-center">
                            {tree.is_latest ? (
                              <div className="inline-flex items-center px-2 py-1 rounded-full bg-maitai-green/10 border border-maitai-green/20">
                                <span className="text-xs font-gc-med text-maitai-green">
                                  Latest
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                -
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="py-4 px-2 text-center">
                            <span className="text-xs font-gc-reg text-foreground">
                              {formatNumber(tree.conversation_flow_count)}
                            </span>
                          </TableCell>
                          <TableCell className="py-4 px-2 text-center">
                            <span className="text-xs font-gc-reg text-foreground">
                              {(() => {
                                const date = new Date(tree.created_at);
                                const now = new Date();
                                const isCurrentYear =
                                  date.getFullYear() === now.getFullYear();

                                if (isCurrentYear) {
                                  return date.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  });
                                } else {
                                  return date.toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  });
                                }
                              })()}
                            </span>
                          </TableCell>
                          <TableCell className="py-4 px-2 text-center max-w-[120px]">
                            <span className="text-xs font-gc-reg text-foreground whitespace-normal break-words leading-tight">
                              {tree.status
                                ? tree.status.replace(/_/g, " ")
                                : "Unknown"}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Pagination Controls */}
              {trees.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, trees.length)} of {trees.length} trees
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className="h-6 px-2">
                      <ChevronLeftIcon className="w-3 h-3" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        const isCurrentPage = pageNum === currentPage;
                        return (
                          <Button
                            key={pageNum}
                            variant={isCurrentPage ? "default" : "ghost"}
                            onClick={() => setCurrentPage(pageNum)}
                            className={cn(
                              "h-6 w-6 p-0 text-xs",
                              isCurrentPage &&
                                "bg-foreground text-background hover:bg-foreground/90"
                            )}>
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="h-6 px-2">
                      <ChevronRightIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Finetune Confirmation Modal */}
      <FinetuneConfirmationModal
        isVisible={isFinetuneModalVisible}
        onClose={handleFinetuneCancel}
        onConfirm={handleFinetuneConfirm}
        loadedTreeMetadata={loadedTreeMetadata}
      />
    </div>
  );
};

export default ConfigTab;
